/**
 * API配置管理
 * 域名为空时自动使用Mock数据
 */

interface ApiConfig {
  PRODUCT_API_BASE: string; // 产品管理API基础地址
  SKU_API_BASE: string; // SKU管理API基础地址（兼容）
  N8N_API_BASE: string; // N8N API基础地址
  MODEL_API_BASE: string; // 模型管理API基础地址
  MODEL_MANAGE_API_BASE: string; // 模型管理后端API基础地址（审核/发布）
  ECS_API_BASE: string; // ECS镜像管理API基础地址
  CONTEST_API_BASE: string; // 竞赛活动API基础地址
  readonly USE_MOCK: boolean; // 是否使用Mock数据
  MODEL_MANAGE_API_BASE_PREVIEW: string; // 模型管理后端API基础地址（审核/发布相关接口测试）
  updateConfig(
    config: Partial<
      Pick<
        ApiConfig,
        | 'PRODUCT_API_BASE'
        | 'SKU_API_BASE'
        | 'N8N_API_BASE'
        | 'MODEL_API_BASE'
        | 'MODEL_MANAGE_API_BASE'
        | 'ECS_API_BASE'
        | 'MODEL_MANAGE_API_BASE_PREVIEW'
        | 'CONTEST_API_BASE'
      >
    >,
  ): void;
}

export const API_CONFIG: ApiConfig = {
  // 产品管理API配置（新）
  PRODUCT_API_BASE: 'https://id.snapmaker.com', // 为空时使用Mock数据
  // PRODUCT_API_BASE: 'http://localhost:8100', // 为空时使用Mock数据

  // SKU相关API配置（兼容现有）
  SKU_API_BASE: 'https://id.snapmaker.com', // 为空时使用Mock数据
  // SKU_API_BASE: 'http://localhost:8100', // 为空时使用Mock数据

  // N8N相关API配置
  N8N_API_BASE: 'https://n8n.kuaizao.org',

  // 模型管理API配置（为空时走proxy代理到本地后端）
  MODEL_API_BASE:
    process.env.MODEL_API_BASE || 'https://api-contest.snapmaker.com',

  // 模型管理后端API配置（审核/发布相关接口）
  MODEL_MANAGE_API_BASE:
    process.env.MODEL_MANAGE_API_BASE || 'https://pre.id.snapmaker.com',
  // 模型管理后端API配置（审核/发布相关接口测试）
  MODEL_MANAGE_API_BASE_PREVIEW: 'https://pre-api-contest.snapmaker.com/api',

  // ECS镜像管理API配置
  ECS_API_BASE: 'https://pre.api.snapmaker.com/api', // 生产环境
  // ECS_API_BASE: '', // 本地开发，走proxy代理

  // 竞赛活动API配置
  CONTEST_API_BASE:
    process.env.CONTEST_API_BASE || 'https://api-contest.snapmaker.com/api',

  // 自动判断是否使用Mock
  get USE_MOCK() {
    return !this.PRODUCT_API_BASE && !this.SKU_API_BASE;
  },

  // 更新配置方法
  updateConfig(
    config: Partial<
      Pick<
        ApiConfig,
        | 'PRODUCT_API_BASE'
        | 'SKU_API_BASE'
        | 'N8N_API_BASE'
        | 'MODEL_MANAGE_API_BASE'
        | 'ECS_API_BASE'
        | 'CONTEST_API_BASE'
        | 'MODEL_MANAGE_API_BASE_PREVIEW'
      >
    >,
  ) {
    Object.assign(this, config);
  },
};

// 获取完整API地址
export const getApiUrl = (path: string, useProduct = true): string => {
  const baseUrl = useProduct
    ? API_CONFIG.PRODUCT_API_BASE
    : API_CONFIG.SKU_API_BASE;
  if (!baseUrl) {
    return path; // Mock模式下返回相对路径
  }
  return `${baseUrl}${path}`;
};

// 数据转换工具函数
export const convertProductToSKUItem = (product: any, siteStock?: any): any => {
  return {
    id: product.id.toString(),
    sku_code: product.sku_id,
    en_title: product.en_title,
    cn_title: product.cn_title,
    product_name: product.cn_title,
    type: product.type,
    variant_name: siteStock?.variant_name,
    category: siteStock?.category,
    images: product.img ? [product.img] : [],
    is_sales_sku: !!product.is_sales,
    is_logistics_sku: !!product.is_logistics,
    price: siteStock ? parseFloat(siteStock.sale_price) : 0,
    sale_price: siteStock ? parseFloat(siteStock.sale_price) : undefined,
    original_price: siteStock
      ? parseFloat(siteStock.original_price)
      : undefined,
    currency: siteStock?.currency,
    stock: {
      available: siteStock?.stock || 0,
      reserved: 0,
      total: siteStock?.stock || 0,
    },
    site_id: siteStock?.site_id?.toString(),
    sales_link: siteStock?.sales_link,
    status: 'active' as const,
    created_at: product.create_time,
    updated_at: product.update_time,
    last_modified_by: product.last_modified_by,
  };
};

// SKU数据转换为Product数据
export const convertSKUItemToProduct = (skuItem: any): any => {
  return {
    sku_id: skuItem.sku_code,
    en_title: skuItem.en_title,
    cn_title: skuItem.cn_title,
    type: skuItem.type || 0,
    img: skuItem.images?.[0] || '',
    is_sales: skuItem.is_sales_sku,
    is_logistics: skuItem.is_logistics_sku,
  };
};

// Mock站点数据
export const MOCK_SITES = [
  {
    id: 1,
    name: 'Snapmaker US Store',
    origin: 'https://us.snapmaker.com',
    webhook_secret: 'mock_secret_1',
    api_secret: 'mock_api_1',
    type: 0, // Shopify
    status: 0, // 正常
    remark: 'US market',
    is_delete: false,
    create_date: '2024-01-01T00:00:00Z',
    modified_date: '2024-01-01T00:00:00Z',
  },
  {
    id: 2,
    name: 'Snapmaker EU Store',
    origin: 'https://eu.snapmaker.com',
    webhook_secret: 'mock_secret_2',
    api_secret: 'mock_api_2',
    type: 0, // Shopify
    status: 0, // 正常
    remark: 'EU market',
    is_delete: false,
    create_date: '2024-01-01T00:00:00Z',
    modified_date: '2024-01-01T00:00:00Z',
  },
];

// API路径配置 - 基于真实数据库表结构
export const API_PATHS = {
  // 产品基础管理 (基于 sku_info 表)
  PRODUCTS: '/api/manage/products',
  PRODUCT_DETAIL: '/api/manage/products', // GET /api/manage/products/{id}
  PRODUCT_CREATE: '/api/manage/products', // POST /api/manage/products
  PRODUCT_UPDATE: '/api/manage/products', // PUT /api/manage/products/{id}
  PRODUCT_DELETE: '/api/manage/products', // DELETE /api/manage/products/{id}

  // 库存管理 (基于 sku_site_stock 表)
  PRODUCT_STOCK: '/api/manage/products/stock/{skuId}', // GET/PUT
  BATCH_STOCK_UPDATE: '/api/manage/products/batch/stock', // PUT

  // 价格管理
  PRODUCT_PRICE: '/api/manage/products/price/{skuId}', // PUT
  BATCH_PRICE_UPDATE: '/api/manage/products/batch/price', // PUT

  // 站点管理 (基于 site_info 表)
  SITES: '/api/manage/sites', // GET
  SITE_DETAIL: '/api/manage/sites', // GET /api/manage/sites/{id}

  // 销售状态管理
  BATCH_SALES_STATUS: '/api/manage/products/batch/sales-status', // PUT

  // 库存列表
  INVENTORY_LIST: '/api/manage/products/stock',

  // N8N相关路径
  UPLOAD_FILES: '/webhook/upload/files',
  LIST_FILES: '/webhook/list/files',

  // N8N Webhook路径 (对应 N8N_API_BASE)
  N8N_POINTS_MANAGEMENT: '/webhook/admin/points/management',
  N8N_DASHBOARD_DEVICE_STATS: '/webhook/admin/dashboard/device-stats',
  N8N_DASHBOARD_DEVICE_TREND: '/webhook/admin/dashboard/device-trend',

  // 固件管理
  FIRMWARE_LIST: '/api/manage/firmwares',
  FIRMWARE_DETAIL: '/api/manage/firmwares/{id}',
  FIRMWARE_CREATE: '/api/manage/firmwares',
  FIRMWARE_UPDATE: '/api/manage/firmwares/{id}',
  FIRMWARE_PUBLISH: '/api/manage/firmwares/{id}/publish',
  FIRMWARE_DISABLE: '/api/manage/firmwares/{id}/disable',
  FIRMWARE_VALIDATE_SNS: '/api/manage/firmwares/validate-sns',

  // ECS镜像管理
  ECS_TASKS: '/manage/ecs',

  // 设备管理
  DEVICE_MODELS: '/api/manage/device/models',
  DEVICE_LIST: '/api/manage/device',
  DEVICE_DETAIL: '/api/manage/device/{id}',
  DEVICE_CREATE: '/api/manage/device',
  DEVICE_UPDATE: '/api/manage/device/{id}',
  DEVICE_DELETE: '/api/manage/device/{id}',
  DEVICE_RESET_MQTT_KEY_WEBHOOK: '/webhook/device/secret/reset',

  // 打印模型管理
  PRINT_MODEL_LIST: '/api/model/list',
  PRINT_MODEL_DETAIL: '/api/model/detail/{id}',
  PRINT_MODEL_CREATE: '/api/manage/model',
  PRINT_MODEL_UPDATE: '/api/manage/model/{id}',
  PRINT_MODEL_DELETE: '/api/manage/model/{id}',
  PRINT_MODEL_TOGGLE_TOP: '/api/manage/model/{id}/toggle-top',
  PRINT_MODEL_PARSE_3MF: '/api/manage/model/parse-3mf',
};

// API路径工具函数
export const buildApiPath = (
  template: string,
  params: Record<string, string | number>,
): string => {
  let path = template;
  Object.entries(params).forEach(([key, value]) => {
    path = path.replace(`{${key}}`, String(value));
  });
  return path;
};
